import {
  ArrowLeft,
  ChevronDown,
  CircleCheck,
  CircleDashed,
  Download,
  Megaphone,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils/cn";
import { IssueIcon, thematicClasses } from "@/lib/thematic";
import { ResourceMenu } from "@/features/resources";

import {
  downloadResultsPdf,
  useProgress,
  useResourceDirectoryFlags,
  useResults,
} from "../api";
import { ErrorBlock, LoadingBlock } from "../components/QueryState";
import type { ResultAnswer, ResultIssue, ResultThematic } from "../types";
import { DemoContentNotice } from "./AnsweringStep";

interface ResultsStepProps {
  onBack: () => void;
  onExploreIssues: () => void;
}

export function ResultsStep({ onBack, onExploreIssues }: ResultsStepProps) {
  const { t, i18n } = useTranslation();
  const results = useResults();
  const progress = useProgress();
  const [downloading, setDownloading] = useState(false);
  // Detail bands default expanded (parity: the old app shows everything); users
  // may collapse individual themes. Membership = collapsed.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  const assessmentDate = new Intl.DateTimeFormat(i18n.language, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());

  // ResultThematic/ResultIssue don't carry the thematic css_class or issue icon,
  // so join them from the progress tree (same ids).
  const cssById = useMemo(() => {
    const map = new Map<string, string>();
    for (const th of progress.data?.thematics ?? []) map.set(th.id, th.css_class);
    return map;
  }, [progress.data]);
  const iconById = useMemo(() => {
    const map = new Map<string, string>();
    for (const th of progress.data?.thematics ?? [])
      for (const issue of th.issues) map.set(issue.id, issue.icon);
    return map;
  }, [progress.data]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadResultsPdf(t("questionnaire.results.pdfFilename"));
    } catch {
      toast.error(t("questionnaire.results.pdfError"));
    } finally {
      setDownloading(false);
    }
  };

  const toggleTheme = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Jump nav: reveal the target band (if collapsed) then scroll it into view.
  const jumpToTheme = (id: string) => {
    setCollapsed((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    requestAnimationFrame(() => {
      const el = document.getElementById(`result-theme-${id}`);
      if (!el) return;
      const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    });
  };

  if (results.isLoading) return <LoadingBlock />;
  if (results.error) return <ErrorBlock error={results.error} />;

  const data = results.data ?? [];
  if (data.length === 0) {
    return (
      <Card>
        <CardContent className="space-y-3 py-8">
          {/* A restricted account with nothing designated is sent straight
              here, so this empty page is the first thing it sees. The Django
              results page carries the same notice. */}
          <DemoContentNotice counts={progress.data?.demo_content_counts} />
          <p className="text-sm text-muted-foreground">
            {t("questionnaire.results.empty")}
          </p>
        </CardContent>
      </Card>
    );
  }

  const pillars = data.map((th) => ({
    id: th.thematic_id,
    name: th.thematic_name,
    cssClass: cssById.get(th.thematic_id),
    carried: sumOverIssues(th.issues, issueCarried),
    course: sumOverIssues(th.issues, issueCourse),
  }));
  const totalCarried = pillars.reduce((a, p) => a + p.carried, 0);
  const totalCourse = pillars.reduce((a, p) => a + p.course, 0);
  const maxTotal = Math.max(1, ...pillars.map((p) => p.carried + p.course));

  return (
    <div className="space-y-6">
      <DemoContentNotice counts={progress.data?.demo_content_counts} />
      {/* Overview hero — the dated synthesis: grand totals (which also key the
          green/cyan colours) + a per-pillar breakdown of the same counts. */}
      <div className="relative overflow-hidden rounded-xl border bg-card p-6 shadow-sm">
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-theme-governance via-theme-economic to-theme-environment"
        />
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
          {t("questionnaire.results.datedHeading", { date: assessmentDate })}
        </p>
        <div className="mt-4 grid gap-x-10 gap-y-6 lg:grid-cols-[auto_1fr] lg:items-center">
          <div className="flex gap-10">
            <Stat
              value={totalCarried}
              label={t("questionnaire.results.statCarried")}
              className="text-success"
            />
            <Stat
              value={totalCourse}
              label={t("questionnaire.results.statCourse")}
              className="text-theme-economic-ink"
            />
          </div>
          {pillars.length > 1 && (
            <div className="lg:border-l lg:pl-10">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t("questionnaire.results.byTheme")}
              </p>
              <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
                {pillars.map((p) => (
                  <ResultPillar key={p.id} {...p} maxTotal={maxTotal} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button asChild>
          <Link to="/action-plans">{t("questionnaire.results.generateActionPlan")}</Link>
        </Button>
        <Button variant="outline" onClick={() => void handleDownload()} disabled={downloading}>
          <Download aria-hidden />
          {downloading
            ? t("questionnaire.results.pdfDownloading")
            : t("questionnaire.results.downloadPdf")}
        </Button>
        {/* Old app gates "Communiquer mes réalisations" on ≥1 carried-out action. */}
        {totalCarried > 0 && (
          <Button variant="outline" asChild>
            <Link to="/communications">
              <Megaphone aria-hidden />
              {t("questionnaire.results.buildShowcase")}
            </Link>
          </Button>
        )}
        <Button variant="ghost" onClick={onExploreIssues}>
          {t("questionnaire.results.exploreIssues")}
        </Button>
      </div>

      {/* Detailed report */}
      <div>
        <div className="mb-3 flex items-center gap-3">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {t("questionnaire.results.detailTitle")}
          </p>
          <span aria-hidden className="h-px flex-1 bg-border" />
        </div>

        {/* Jump nav — scan the themes, jump to (and reveal) one. */}
        {data.length > 1 && (
          <nav
            aria-label={t("questionnaire.results.jumpNav")}
            className="mb-4 flex flex-wrap gap-2"
          >
            {data.map((thematic) => {
              const tc = thematicClasses(cssById.get(thematic.thematic_id));
              return (
                <button
                  key={thematic.thematic_id}
                  type="button"
                  onClick={() => jumpToTheme(thematic.thematic_id)}
                  className="inline-flex min-h-touch items-center gap-2 rounded-full border bg-card px-3 py-1.5 text-sm font-semibold text-foreground/80 shadow-sm transition-colors hover:bg-secondary hover:text-foreground"
                >
                  <span aria-hidden className={cn("size-2.5 rounded-sm", tc.solid)} />
                  {thematic.thematic_name}
                </button>
              );
            })}
          </nav>
        )}

        <div className="space-y-3">
          {data.map((thematic) => (
            <DetailBand
              key={thematic.thematic_id}
              thematic={thematic}
              cssClass={cssById.get(thematic.thematic_id)}
              iconById={iconById}
              collapsed={collapsed.has(thematic.thematic_id)}
              onToggle={toggleTheme}
            />
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden />
        {t("questionnaire.results.backToAnswering")}
      </button>
    </div>
  );
}

function Stat({
  value,
  label,
  className,
}: {
  value: number | string;
  label: string;
  className?: string;
}) {
  return (
    <div>
      <div className={cn("text-4xl font-extrabold leading-none tabular-nums", className)}>
        {value}
      </div>
      <div className="mt-1.5 text-xs font-medium text-muted-foreground">{label}</div>
    </div>
  );
}

// --- Pillar overview ----------------------------------------------------------

/**
 * One thematic's carried/course mix as a two-segment bar scaled to the busiest
 * thematic (`maxTotal`), so bar length reads as volume and the split reads as
 * carried-out (success) vs course-of-action (economic). Same colour language as
 * the count chips and the answering step.
 */
function ResultPillar({
  name,
  cssClass,
  carried,
  course,
  maxTotal,
}: {
  name: string;
  cssClass?: string;
  carried: number;
  course: number;
  maxTotal: number;
}) {
  const tc = thematicClasses(cssClass);
  return (
    <div className="flex items-center gap-2.5">
      <span aria-hidden className={cn("size-2.5 flex-none rounded-sm", tc.solid)} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center justify-between gap-2">
          <span className="truncate text-xs font-semibold text-foreground/80">{name}</span>
          <span className="text-xs tabular-nums text-muted-foreground">
            {carried} · {course}
          </span>
        </span>
        <span className="mt-1 flex h-2 w-full overflow-hidden rounded-full bg-secondary">
          <span
            className="h-full bg-success"
            style={{ width: `${(carried / maxTotal) * 100}%` }}
          />
          <span
            className="h-full bg-theme-economic"
            style={{ width: `${(course / maxTotal) * 100}%` }}
          />
        </span>
      </span>
    </div>
  );
}

// --- Count chips --------------------------------------------------------------

/** Carried-out (green ✓) and course-of-action (cyan ◌) counts, labelled for AT. */
function CountChips({ carried, course }: { carried: number; course: number }) {
  const { t } = useTranslation();
  const carriedLabel = t("questionnaire.results.legendCarried");
  const courseLabel = t("questionnaire.results.legendCourse");
  return (
    <span className="flex shrink-0 items-center gap-1.5">
      <span
        title={carriedLabel}
        aria-label={`${carried} ${carriedLabel}`}
        className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-xs font-bold tabular-nums text-success"
      >
        <CircleCheck className="size-3.5" aria-hidden />
        {carried}
      </span>
      <span
        title={courseLabel}
        aria-label={`${course} ${courseLabel}`}
        className="inline-flex items-center gap-1 rounded-full bg-theme-economic-soft px-2 py-0.5 text-xs font-bold tabular-nums text-theme-economic-ink"
      >
        <CircleDashed className="size-3.5" aria-hidden />
        {course}
      </span>
    </span>
  );
}

// --- Detailed report ----------------------------------------------------------

function DetailBand({
  thematic,
  cssClass,
  iconById,
  collapsed,
  onToggle,
}: {
  thematic: ResultThematic;
  cssClass?: string;
  iconById: Map<string, string>;
  collapsed: boolean;
  onToggle: (id: string) => void;
}) {
  const tc = thematicClasses(cssClass);
  const carried = sumOverIssues(thematic.issues, issueCarried);
  const course = sumOverIssues(thematic.issues, issueCourse);
  const bodyId = `result-theme-body-${thematic.thematic_id}`;
  return (
    <div
      id={`result-theme-${thematic.thematic_id}`}
      className="scroll-mt-6 overflow-hidden rounded-xl border bg-card shadow-sm"
    >
      <button
        type="button"
        onClick={() => onToggle(thematic.thematic_id)}
        aria-expanded={!collapsed}
        aria-controls={bodyId}
        className={cn(
          "relative flex w-full items-center gap-3 py-3.5 pl-6 pr-4 text-left transition-colors",
          tc.softBg,
        )}
      >
        <span aria-hidden className={cn("absolute inset-y-0 left-0 w-1.5", tc.solid)} />
        <span className={cn("flex-1 text-base font-bold", tc.ink)}>
          {thematic.thematic_name}
        </span>
        <CountChips carried={carried} course={course} />
        <ChevronDown
          aria-hidden
          className={cn(
            "size-5 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none",
            !collapsed && "rotate-180",
          )}
        />
      </button>
      {/* Kept mounted (toggled via `hidden`) so `aria-controls` always resolves. */}
      <div id={bodyId} hidden={collapsed} className="divide-y">
        {thematic.issues.map((issue) => (
          <IssueDetail
            key={issue.issue_id}
            issue={issue}
            cssClass={cssClass}
            iconById={iconById}
          />
        ))}
      </div>
    </div>
  );
}

function IssueDetail({
  issue,
  cssClass,
  iconById,
}: {
  issue: ResultIssue;
  cssClass?: string;
  iconById: Map<string, string>;
}) {
  const { t } = useTranslation();
  const tc = thematicClasses(cssClass);
  return (
    <div className="px-5 py-4">
      <div className="mb-3 flex items-center gap-3">
        <span
          aria-hidden
          className={cn("grid size-8 shrink-0 place-items-center rounded-lg", tc.softBg, tc.ink)}
        >
          <IssueIcon icon={iconById.get(issue.issue_id)} className="size-4" />
        </span>
        <span className="flex-1 text-sm font-bold leading-tight">{issue.issue_name}</span>
        <CountChips carried={issueCarried(issue)} course={issueCourse(issue)} />
      </div>
      <div className="space-y-6 sm:pl-11">
        {issue.goals.map((goal) => (
          <div key={goal.goal_id}>
            {/* Goal grouping — parity with the old app's "Objectif #N" heading. */}
            <p className="mb-2.5 flex flex-wrap items-baseline gap-x-2">
              <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                {t("questionnaire.results.goalNumber", { number: goal.order + 1 })}
              </span>
              <span className="text-sm font-semibold">{goal.goal_name}</span>
            </p>
            <div className="grid gap-5 md:grid-cols-2">
              <AnswerColumn
                title={t("questionnaire.results.carriedOut")}
                answers={goal.carried_out}
                icon={<CircleCheck className="size-4 text-success" aria-hidden />}
                emptyLabel={t("questionnaire.results.noneCarriedOut")}
              />
              <AnswerColumn
                title={t("questionnaire.results.courseOfAction")}
                answers={goal.course_of_action}
                icon={<CircleDashed className="size-4 text-theme-economic-ink" aria-hidden />}
                emptyLabel={t("questionnaire.results.noneCourseOfAction")}
                showResources
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnswerColumn({
  title,
  answers,
  icon,
  emptyLabel,
  showResources = false,
}: {
  title: string;
  answers: ResultAnswer[];
  icon: React.ReactNode;
  emptyLabel: string;
  /**
   * Offer the Ressources entry point on each row. Only the pistes d'action
   * column does — an action already carried out needs no support finding.
   */
  showResources?: boolean;
}) {
  const directories = useResourceDirectoryFlags();
  return (
    <div>
      <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
        {icon}
        {title}
        <span className="font-normal text-muted-foreground">({answers.length})</span>
      </p>
      {answers.length > 0 ? (
        <ul className="space-y-2">
          {answers.map((answer) => (
            <li key={answer.id} className="text-sm">
              <span className="flex items-start gap-1.5">
                <span className="flex-1">{answer.action_name}</span>
                {showResources && (
                  <ResourceMenu
                    actionId={answer.action_id}
                    actionName={answer.action_name}
                    directories={directories}
                    availability={answer}
                    variant="short"
                    favoritesDefault={false}
                  />
                )}
              </span>
              {answer.comment && (
                <p className="mt-0.5 text-muted-foreground">{answer.comment}</p>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">{emptyLabel}</p>
      )}
    </div>
  );
}

/** Carried-out / course-of-action counts for an issue, summed across its goals. */
function issueCarried(issue: ResultIssue): number {
  return issue.goals.reduce((a, g) => a + g.carried_out.length, 0);
}
function issueCourse(issue: ResultIssue): number {
  return issue.goals.reduce((a, g) => a + g.course_of_action.length, 0);
}

/** Sum a per-issue metric across a list of issues. */
function sumOverIssues(
  issues: ResultIssue[],
  fn: (issue: ResultIssue) => number,
): number {
  return issues.reduce((a, issue) => a + fn(issue), 0);
}
